<template>
  <Flex
    direction="column"
    fullWidth
  >
    <Flex
      direction="column"
      :gap=".25"
    >
      <BaseHeading size="l">
        {{ $t('register.salonized_pay.dashboard.overview.heading') }}
      </BaseHeading>
      <BaseText color="secondary">
        {{ $t('register.salonized_pay.dashboard.overview.subheading') }}
      </BaseText>
    </Flex>

    <BaseTask
      v-if="adyenVerficationStatus.state !== 'completed'"
      :text="adyenVerficationStatus.text as string"
      :heading="adyenVerficationStatus.heading"
      :state="adyenVerficationStatus.state"
      :buttonIcon="adyenVerficationStatus.buttonIcon"
      :loading="loading"
      @click="$emit('generateOnboardingLink')"
      @buttonClick="$emit('generateOnboardingLink')"
      v-test="`sp-verification-${adyenVerficationStatus.state}`"
    />

    <BaseButton
      v-if="adyenAppState.verificationStatus !== 'VALID' && vimeoId"
      :href="`https://vimeo.com/${vimeoId}`"
      color="inverted"
      v-test="'sp-button-youtube'"
    >
      {{ $t('register.salonized_pay.landing.watch_video') }}
    </BaseButton>

    <BaseAlert
      v-if="showFailureAlert"
      color="warning"
      :text="$t('register.salonized_pay.profile.failure_alert.text')"
      v-test="'sp-order-failure-alert'"
    />

    <BaseCard
      v-if="shippingTerminals.length || shippingTerminalOrders.length"
      :heading="$t('global.pages.pos_terminals')"
      v-test="'sp-overview-terminal-shipments'"
    >
      <template #header>
        <BaseText
          @click="$emit('seeAllTerminals')"
          v-test="'sp-overview-terminals-see-all'"
        >
          {{ $t('global.see_all') }}
        </BaseText>
      </template>
      <Terminals
        :terminals="shippingTerminals"
        :terminalOrders="shippingTerminalOrders"
      />
    </BaseCard>

    <BaseCard
      v-if="hasData && showBalance"
      :heading="$t('register.salonized_pay.dashboard.your_money')"
      v-test="'sp-dashboard-stats'"
    >
      <div :class="$style.amounts">
        <Flex
          direction="column"
          :gap=".5"
        >
          <BaseHeading size="s">
            {{ $t('register.salonized_pay.dashboard.next_payout') }}
          </BaseHeading>
          <BaseText
            size="l"
            bold
            v-test="'sp-dashboard-available-balance'"
          >
            {{ filters.currency(availableBalance) }}
          </BaseText>
        </Flex>
        <Flex
          direction="column"
          :gap=".5"
        >
          <BaseHeading size="s">
            {{ $t('register.salonized_pay.dashboard.processing') }}
          </BaseHeading>
          <BaseText
            size="l"
            bold
            v-test="'sp-dashboard-processing-balance'"
          >
            {{ filters.currency(processingBalance) }}
          </BaseText>
        </Flex>
      </div>
    </BaseCard>

    <BaseCard
      v-if="unleash.isEnabled('SalonizedPayTerminalTipping') && hasTerminal && adyenAppState.terminalTippingEnabled"
      :heading="$t('register.salonized_pay.dashboard.tips_heading')"
    >
      <template #header>
        <BaseText
          @click="showPosPaymentsModal = true"
          v-test="'sp-tips-see-all'"
        >
          {{ $t('global.see_all') }}
        </BaseText>
      </template>
      <div :class="$style.amounts">
        <Flex
          direction="column"
          :gap=".5"
        >
          <BaseHeading size="s">
            {{ $t('reports.last_month') }}
          </BaseHeading>
          <BaseText
            size="l"
            bold
          >
            {{ filters.currency(previousMonthTips) }}
          </BaseText>
        </Flex>
        <Flex
          direction="column"
          :gap=".5"
        >
          <BaseHeading size="s">
            {{ $t('reports.this_month') }}
          </BaseHeading>
          <BaseText
            size="l"
            bold
          >
            {{ filters.currency(currentMonthTips) }}
          </BaseText>
        </Flex>
      </div>
    </BaseCard>

    <BaseCard
      v-if="hasData && showBalance"
      :heading="$t('register.salonized_pay.dashboard.recent_payouts')"
    >
      <template #header>
        <BaseText
          @click="$emit('seeAllPayouts')"
          v-test="'sp-recent-payouts-see-all'"
        >
          {{ $t('global.see_all') }}
        </BaseText>
      </template>
      <EmptyPageContent
        v-if="!recentPayoutsLoading && !recentPayouts.length"
        imageName="history"
        :text="{
          title: $t('register.salonized_pay.dashboard.payout_history.empty_state.title'),
          description: $t('register.salonized_pay.dashboard.payout_history.empty_state.description')
        }"
      />
      <BaseTable
        v-else
        :headers="[$t('global.date'), $t('global.amount'), $t('global.status')]"
        :rows="recentPayoutRows"
        v-test="'sp-recent-payouts-table'"
      />
    </BaseCard>

    <OnlinePayments v-if="unleash.isEnabled('SalonizedPayOnline')" />

    <BaseCard
      v-if="isVerifying"
      gray
      v-test="'sp-dashboard-start'"
    >
      <div :class="$style.verifying">
        <img src="/img/salonized-pay/onboarding/verifying.svg">
        <BaseText>
          {{ $t('register.salonized_pay.dashboard.verifying', brandNamePay()) }}
        </BaseText>
      </div>
    </BaseCard>

    <PosPaymentsModal
      v-if="showPosPaymentsModal"
      :startDate="currentMonthStart.format('YYYY-MM-DD')"
      :endDate="currentMonthEnd.format('YYYY-MM-DD')"
      @close="showPosPaymentsModal = false"
    />
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import PosPaymentsModal from '@/modules/reports/totals/PosPaymentsModal.vue';
import { useAdyenStore } from '@/stores/adyen';
import { useUserStore } from '@/stores/user';
import type {
  AdyenPayout,
  AdyenTerminalOrder,
  Currency,
  PosTerminal,
} from '@/types';
import unleash from '@/unleash';
import { vimeoId } from '../../helpers';
import { GET_ADYEN_PAYOUTS, GET_ADYEN_TIPS_DATA } from '../graphql';
import OnlinePayments from './OnlinePayments.vue';
import Terminals from './terminals/index.vue';

defineProps<{
  availableBalance: number;
  processingBalance: number;
  showBalance: boolean;
  hasData: boolean;
  isVerifying: boolean;
  hasTerminal: boolean;
  shippingTerminals: PosTerminal[];
  shippingTerminalOrders: AdyenTerminalOrder[];
  loading: boolean;
}>();

defineEmits(['generateOnboardingLink', 'seeAllPayouts', 'seeAllTerminals']);

const { t } = useI18n();
const { adyenAppState } = useAdyenStore();
const { hasFeatureFlag } = useUserStore();

const verificationStatus = computed(() => {
  switch (adyenAppState.verificationStatus) {
    case 'VALID':
      return {
        heading: t('register.salonized_pay.verification.pending.heading'),
        text: t('register.salonized_pay.verification.valid.description'),
        buttonIcon: 'edit',
        state: 'completed',
      };
    case 'PENDING':
      return {
        heading: t('register.salonized_pay.verification.pending.heading'),
        text: t('register.salonized_pay.verification.pending.description'),
        buttonIcon: 'edit',
        state: 'pending',
      };
    case 'INVALID':
      return {
        heading: t('register.salonized_pay.verification.pending.heading'),
        text: t('register.salonized_pay.verification.invalid.description'),
        state: 'attention',
      };
    default:
      return {
        heading: t('register.salonized_pay.verification.pending.heading'),
        text: t('register.salonized_pay.verification.pending.description'),
        buttonIcon: 'edit',
        state: 'pending',
      };
  }
});

// Only owners and admins can open the hosted onboarding, so others get no way in.
const adyenVerficationStatus = computed(() => {
  if (hasFeatureFlag('admin-salonized-pay')) {
    return verificationStatus.value;
  }

  const status = { ...verificationStatus.value, buttonIcon: undefined };
  return status.state === 'attention'
    ? {
        ...status,
        text: t('register.salonized_pay.verification.ask_admin'),
        state: 'blocked',
      }
    : status;
});

// Query params are consumed once, here, since this alert is Overview-only.
const route = useRoute();
const router = useRouter();
const showFailureAlert = ref(false);

if (route.query.payment === 'failure') {
  showFailureAlert.value = true;
  router.replace({ name: 'salonized-pay' });
}

const dayStart = dayjs().startOf('day');
const dayEnd = dayjs().endOf('day');
const currentMonthStart = dayStart.startOf('month');
const currentMonthEnd = dayEnd.endOf('month');
const previousMonthStart = dayStart.subtract(1, 'month').startOf('month');
const previousMonthEnd = dayEnd.subtract(1, 'month').endOf('month');

const { result: currentMonthTipsResult } = useQuery(GET_ADYEN_TIPS_DATA, {
  startDate: currentMonthStart.format(),
  endDate: currentMonthEnd.format(),
});

const { result: previousMonthTipsResult } = useQuery(GET_ADYEN_TIPS_DATA, {
  startDate: previousMonthStart.format(),
  endDate: previousMonthEnd.format(),
});

const currentMonthTips = computed(
  () => currentMonthTipsResult.value?.adyenDashboardData?.tips || 0,
);

const previousMonthTips = computed(
  () => previousMonthTipsResult.value?.adyenDashboardData?.tips || 0,
);

const showPosPaymentsModal = ref(false);

const { result: recentPayoutsResult, loading: recentPayoutsLoading } = useQuery(
  GET_ADYEN_PAYOUTS,
  {
    pagination: { currentPage: 1, perPage: 4 },
  },
);

const recentPayouts = computed<AdyenPayout[]>(
  () => recentPayoutsResult.value?.adyenPayouts ?? [],
);

const statusColor = (status?: string | null) => {
  switch (status) {
    case 'booked':
      return 'success';
    case 'failed':
      return 'error';
    case 'returned':
      return 'warning';
    default:
      return 'default';
  }
};

const statusText = (status?: string | null) =>
  status
    ? t(`register.salonized_pay.dashboard.payout_history.status.${status}`)
    : '-';

const recentPayoutRows = computed(() =>
  recentPayouts.value.map((payout) => ({
    id: payout.id,
    cells: [
      filters.date(payout.transferredAt || ''),
      filters.currency(
        payout.amountValue ?? 0,
        payout.amountCurrency as Currency,
      ),
      {
        label: {
          text: statusText(payout.status),
          color: statusColor(payout.status),
        },
      },
    ],
  })),
);
</script>

<style
  lang="scss"
  module
>
.amounts {
  display: flex;
  gap: $spacing * 2;
}

.verifying {
  display: flex;
  align-items: center;
  gap: $spacing;
}
</style>
